import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import PropTypes from 'prop-types';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import style from './EquipmentPanel.module.css';
import {
  adaptersResourceInitialState,
  adaptersResourceType,
} from '../../../entities/equipments/adaptersResource/redux/adaptersResource.slice';
import { adapterResourceType } from '../../../entities/equipments/adapterResourceType/redux/adapterResourceType.slice';
import SingleSelect from '../../../shared/ui/SingleSelect';
import ThresholdSliderWidget from '../../../widgets/intervals/ThresholdSliderWidget/ThresholdSliderWidget';
import {
  useGetAdapterConfigItemsQuery,
  useGetAdapterConfigsQuery,
} from '../../../entities/equipments/adapterConfigResource/redux/adapterConfigResource.api';

function EquipmentPanel({ data, patchAdapterParams, postAdapterParams, vehicleId }) {
  const [isEditing, setIsEditing] = useState(false);
  const [adapter, setAdapter] = useState(null);

  const [vehicleConfigState, setVehicleConfigState] = useState({});

  const [isErrAdapter, setIsErrAdapter] = useState(false);
  const [isCorrectAdapter, setIsCorrectAdapter] = useState(false);

  const { dataAdapterTypes, dataAdapters, sensorValueTypes } = data;

  const sortedAdapterTypes = dataAdapterTypes?.data?.slice().sort((a, b) => a.model.localeCompare(b.model));

  const adapterTypeId = adapter?.type?.adapterTypeId;
  const { data: adapterConfigs, isLoading: isAdapterConfigsLoading } = useGetAdapterConfigsQuery(
    { 'adapterTypeId.equals': adapterTypeId },
    { skip: !adapterTypeId },
  );

  const [activeConfig, setActiveConfig] = useState(null);

  const { adapterConfigId } = activeConfig || {};
  const { data: configItemsRaw, isFetching: isConfigItemsFetching } = useGetAdapterConfigItemsQuery(
    adapterConfigId ? { 'configId.equals': adapterConfigId } : skipToken,
  );
  const configItems = !adapterConfigId || isConfigItemsFetching ? [] : configItemsRaw;

  // vehicle_config_items извлекаются из загруженного адаптера
  const vehicleConfigItems = adapter?.vehicleConfig || [];

  // карта значений по-умолчанию из adapter_config_items по sensorTypeId
  const configItemDefaultsMap = useMemo(() => {
    const map = {};
    (configItems || []).forEach((item) => {
      const sensorTypeId = item.sensorValueType?.sensorTypeId;
      if (sensorTypeId) {
        map[sensorTypeId] = item;
      }
    });
    return map;
  }, [configItems]);

  // карта индивидуальных значений ТС из vehicle_config_items по sensorTypeId
  const vehicleConfigMap = useMemo(() => {
    const map = {};
    (vehicleConfigItems || []).forEach((item) => {
      const sensorTypeId = item.sensorValueType?.sensorTypeId;
      if (sensorTypeId) {
        map[sensorTypeId] = item;
      }
    });
    return map;
  }, [vehicleConfigItems]);

  // сенсоры из конфигурации
  const sensorsFromConfig = useMemo(() => {
    if (!activeConfig || !configItems) return [];

    const list =
      sensorValueTypes.filter((svt) =>
        configItems?.some((ci) => ci.sensorValueType.sensorTypeId === svt.sensorTypeId),
      ) || [];

    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [activeConfig, configItems, sensorValueTypes]);

  // стабильный ключ конфигурации: при его смене пересобираем vehicleConfigState из defaults
  const configKey = useMemo(() => {
    if (!sensorsFromConfig.length) return 'empty';
    const ids = sensorsFromConfig.map((s) => s.sensorTypeId).sort().join(',');
    return `${ids}|${activeConfig?.adapterConfigId || 'none'}`;
  }, [sensorsFromConfig, activeConfig?.adapterConfigId]);

  const prevConfigKey = useRef(configKey);

  // инициализация / сброс vehicleConfigState только при реальной смене конфигурации
  useEffect(() => {
    if (prevConfigKey.current === configKey) return;
    prevConfigKey.current = configKey;

    if (!sensorsFromConfig.length) {
      setVehicleConfigState({});
      return;
    }

    const nextState = {};
    sensorsFromConfig.forEach((sensor) => {
      const stId = sensor.sensorTypeId;
      const defaults = configItemDefaultsMap[stId];
      const overrides = vehicleConfigMap[stId];

      const lowWarning = overrides?.lowWarningThresh ?? defaults?.lowWarningThresh ?? 30;
      const highWarning = overrides?.highWarningThresh ?? defaults?.highWarningThresh ?? 70;
      const lowError = overrides?.lowErrorThresh ?? defaults?.lowErrorThresh ?? 10;
      const highError = overrides?.highErrorThresh ?? defaults?.highErrorThresh ?? 90;
      const warningEnabled = overrides?.warningEnable ?? defaults?.warningEnable ?? false;
      const errorEnabled = overrides?.errorEnable ?? defaults?.errorEnable ?? false;

      nextState[stId] = {
        warningEnabled,
        errorEnabled,
        minValue: defaults?.minValue ?? 0,
        maxValue: defaults?.maxValue ?? 100,
        warningLower: lowWarning,
        warningUpper: highWarning,
        errorLower: lowError,
        errorUpper: highError,
        vehicleConfigItemId: overrides?.vehicleConfigItemId || null,
      };
    });

    setVehicleConfigState(nextState);
  }, [configKey, sensorsFromConfig, configItemDefaultsMap, vehicleConfigMap]);

  useEffect(() => {
    setAdapter(null);
    setActiveConfig(null);
    setIsEditing(false);
    setIsErrAdapter(false);
    setIsCorrectAdapter(false);
  }, [vehicleId]);

  // загружаем адаптер
  useEffect(() => {
    const adapterFromApi = dataAdapters?.data?.[0];

    if (adapterFromApi) {
      setAdapter(adapterFromApi);
    } else {
      const custom = JSON.parse(JSON.stringify(adaptersResourceInitialState));
      custom.vehicle.vehicleId = vehicleId;
      setAdapter(custom);
    }
  }, [data, vehicleId]);

  const changeActiveConfig = (configId) => {
    const config = adapterConfigs?.find((c) => c.adapterConfigId === configId);
    setActiveConfig(config);
  };

  useEffect(() => {
    if (adapter) {
      changeActiveConfig(adapter?.adapterConfigs?.adapterConfigId);
    }
  }, [adapter, adapterConfigs]);

  const handleClickPatchDate = () => {
    setIsEditing(false);

    const vehicleConfig = Object.entries(vehicleConfigState).map(([sensorTypeId, th]) => ({
      sensorValueType: { sensorTypeId: Number(sensorTypeId) },
      lowWarningThresh: th.warningLower,
      highWarningThresh: th.warningUpper,
      lowErrorThresh: th.errorLower,
      highErrorThresh: th.errorUpper,
      warningEnable: th.warningEnabled,
      errorEnable: th.errorEnabled,
      ...(th.vehicleConfigItemId ? { vehicleConfigItemId: th.vehicleConfigItemId } : {}),
    }));

    const adapterCurrent = dataAdapters?.data?.[0];

    if (adapterCurrent?.adapterId) {
      const { adapterId: currentAdapterId } = adapterCurrent;

      patchAdapterParams({
        id: currentAdapterId,
        body: {
          ...adapter,
          adapterConfigs: activeConfig,
          vehicleConfig,
        },
      }).then((e) => {
        if (e.error) setIsErrAdapter(true);
        else setIsCorrectAdapter(true);
      });

      return;
    }

    postAdapterParams({
      body: {
        ...adapter,
        adapterConfigs: activeConfig,
        vehicleConfig,
      },
    }).then((e) => {
      if (e.error) setIsErrAdapter(true);
      else setIsCorrectAdapter(true);
    });
  };

  return (
    <Box sx={{ my: 2 }}>
      <Box sx={{ display: 'flex', gap: '50px' }}>
        <Box sx={{ width: '400px' }}>
          <Typography sx={{ mb: 1 }}>Адаптер/Трекер</Typography>

          {!adapter ? (
            <CircularProgress />
          ) : (
            <Box className={style.Details}>
              <TextField
                value={adapter?.brand || ''}
                onChange={(e) => setAdapter({ ...adapter, brand: e.target.value })}
                disabled={!isEditing}
                sx={{ width: '100%' }}
                label="Марка"
                variant="standard"
              />

              <TextField
                value={adapter?.serialNumber || ''}
                onChange={(e) => setAdapter({ ...adapter, serialNumber: e.target.value })}
                disabled={!isEditing}
                sx={{ width: '100%' }}
                label="Модель"
                variant="standard"
              />

              <FormControl fullWidth>
                <InputLabel>Протокол</InputLabel>
                <Select
                  label="Протокол"
                  value={adapter?.type?.adapterTypeId || ''}
                  onChange={(e) => {
                    setAdapter({
                      ...adapter,
                      type: { ...adapter.type, adapterTypeId: e.target.value },
                    });
                    setActiveConfig(null);
                  }}
                  disabled={!isEditing}
                >
                  {sortedAdapterTypes?.map((t) => (
                    <MenuItem key={t.adapterTypeId} value={t.adapterTypeId}>
                      {t.model}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                value={adapter?.token || ''}
                onChange={(e) => setAdapter({ ...adapter, token: e.target.value })}
                disabled={!isEditing}
                sx={{ width: '100%' }}
                label="Код идентификации (IMEI)"
                variant="standard"
              />
            </Box>
          )}

          {!isErrAdapter && isCorrectAdapter && (
            <Alert severity="success">
              <AlertTitle>Успешно</AlertTitle>
              Данные успешно сохранены
            </Alert>
          )}

          {isErrAdapter && (
            <Alert severity="error">
              <AlertTitle>Ошибка</AlertTitle>
              Произошла ошибка
            </Alert>
          )}
        </Box>

        <Box className={style.EquipmentPanel}>
          <Typography>Датчики</Typography>

          <SingleSelect
            label="Конфигурация"
            name="adapterConfig"
            value={activeConfig?.adapterConfigId?.toString() || ''}
            items={(adapterConfigs || []).map((cfg) => ({
              name: cfg.name,
              value: cfg.adapterConfigId.toString(),
            }))}
            onChange={(e) => {
              changeActiveConfig(Number(e.target.value));
            }}
            disabled={!isEditing}
          />

          {isAdapterConfigsLoading || isConfigItemsFetching ? (
            <CircularProgress />
          ) : sensorsFromConfig.length === 0 && !!activeConfig ? (
            <Typography sx={{ opacity: 0.6, mt: 2 }}>Нет сенсоров в конфигурации</Typography>
          ) : (
            sensorsFromConfig.map((sensor) => (
              <SensorAccordionCurrent
                key={sensor.sensorTypeId}
                sensor={sensor}
                threshold={vehicleConfigState[sensor.sensorTypeId]}
                disabled={!isEditing}
                onChange={(thresholdData) => {
                  setVehicleConfigState((prev) => ({
                    ...prev,
                    [sensor.sensorTypeId]: thresholdData,
                  }));
                }}
              />
            ))
          )}
        </Box>
      </Box>
      <Box className={style.AccordionGroupButtons}>
        {isEditing && (
          <Button
            onClick={() => {
              setIsEditing(false);
              const adapterFromApi = dataAdapters?.data?.[0];

              if (!adapterFromApi) return;

              setAdapter(adapterFromApi);
              changeActiveConfig(adapterFromApi?.adapterConfigs?.adapterConfigId);
            }}
            variant="contained"
          >
            ОТМЕНА
          </Button>
        )}

        {!isEditing && (
          <Button onClick={() => setIsEditing(true)} variant="contained">
            РЕДАКТИРОВАТЬ
          </Button>
        )}

        {isEditing && (
          <Button onClick={handleClickPatchDate} variant="contained">
            СОХРАНИТЬ
          </Button>
        )}
      </Box>
    </Box>
  );
}

function SensorAccordionCurrent({ sensor, threshold, disabled, onChange }) {
  if (!threshold) {
    return (
      <Accordion sx={{ width: '100%' }}>
        <AccordionSummary>
          <Box className={style.AccordionCurrent}>
            <GraphicEqIcon />
            <Typography sx={{ flexGrow: 1, ml: 1 }}>{sensor.name}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <CircularProgress />
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Accordion sx={{ width: '100%' }}>
      <AccordionSummary>
        <Box className={style.AccordionCurrent}>
          <GraphicEqIcon />
          <Typography sx={{ flexGrow: 1, ml: 1 }}>{`${sensor.name}`}</Typography>
          {threshold.warningEnabled && <WarningAmberIcon color="warning" sx={{ ml: 1 }} />}
          {threshold.errorEnabled && <ErrorOutlineIcon color="error" sx={{ ml: 0.5 }} />}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Typography sx={{ opacity: 0.7 }}>{sensor.description}. Код {sensor.code}</Typography>
        <ThresholdSliderWidget
          id={`sensor-${sensor.sensorTypeId}`}
          minValue={threshold.minValue}
          maxValue={threshold.maxValue}
          warningLower={threshold.warningLower}
          warningUpper={threshold.warningUpper}
          errorLower={threshold.errorLower}
          errorUpper={threshold.errorUpper}
          warningEnabled={threshold.warningEnabled}
          errorEnabled={threshold.errorEnabled}
          disabled={disabled}
          onChange={(newState) => {
            onChange(newState);
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
}

SensorAccordionCurrent.propTypes = {
  sensor: PropTypes.shape({
    sensorTypeId: PropTypes.number.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    code: PropTypes.string,
    units: PropTypes.string,
  }).isRequired,
  threshold: PropTypes.shape({
    warningEnabled: PropTypes.bool,
    errorEnabled: PropTypes.bool,
    minValue: PropTypes.number,
    maxValue: PropTypes.number,
    warningLower: PropTypes.number,
    warningUpper: PropTypes.number,
    errorLower: PropTypes.number,
    errorUpper: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

SensorAccordionCurrent.defaultProps = {
  threshold: null,
  disabled: false,
  onChange: () => {},
};

EquipmentPanel.propTypes = {
  data: PropTypes.shape({
    dataAdapters: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape(adaptersResourceType)),
    }),
    dataAdapterTypes: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape(adapterResourceType)),
    }),
    dataSensorSources: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape(adapterResourceType)),
    }),
    sensorValueTypes: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  patchAdapterParams: PropTypes.func,
  postAdapterParams: PropTypes.func,
  vehicleId: PropTypes.number,
};

EquipmentPanel.defaultProps = {
  data: null,
  patchAdapterParams: null,
  postAdapterParams: null,
  vehicleId: null,
};

export default EquipmentPanel;
